import React, { useState } from 'react';
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,} from '@/components/ui/dialog';
import {type ForecastData, type ForecastPeriod, type WeatherData} from '@/lib/weather';
import {Area, AreaChart, CartesianGrid, XAxis, YAxis} from 'recharts';
import {ChartContainer, ChartTooltip, type ChartConfig} from '@/components/ui/chart';
import {STONE} from '@/lib/brandColors';
import { CloudRain, CloudSnow, MapPin, Snowflake } from '@phosphor-icons/react';
import type { WeatherForecastAlert } from '@/lib/api/routes/weather-alerts';
import { ForecastTimeline } from '@/components/dispatch/WeatherAlertBanner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface WeatherDetailsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  weather: WeatherData | null;
  forecast: ForecastData | null;
  companyAddress: string;
  weatherAlerts?: WeatherForecastAlert[];
}

const MM_TO_INCHES = 0.0393701;

// Convert wind degrees to compass direction
const getWindDirection = (degrees: number): string => {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(degrees / 45) % 8;
  return directions[index];
};

function formatPrecipInches(inches: number): string {
  if (inches < 0.01) return '0"';
  if (inches < 1) return `${inches.toFixed(2)}"`;
  return `${inches.toFixed(1)}"`;
}

function getWeatherEmoji(condition: string, iconCode?: string): string {
  const c = condition.toLowerCase();
  // OpenWeatherMap icon codes carry a day/night suffix (e.g. '01d' vs '01n').
  const isNight = iconCode?.endsWith('n') ?? false;
  if (c.includes('sunny') || c.includes('clear')) return isNight ? '\uD83C\uDF19' : '\u2600\uFE0F';
  if (c.includes('rain') || c.includes('drizzle')) return '\uD83C\uDF27\uFE0F';
  if (c.includes('snow')) return '\u2744\uFE0F';
  if (c.includes('storm')) return '\u26C8\uFE0F';
  if (c.includes('cloud')) return '\u2601\uFE0F';
  if (c.includes('fog') || c.includes('mist') || c.includes('haze')) return '\uD83C\uDF2B\uFE0F';
  return isNight ? '\uD83C\uDF19' : '\u2600\uFE0F';
}

const snowChartConfig = {
  snow: {
    label: 'Snow',
    color: STONE[400],
  },
} satisfies ChartConfig;

const rainChartConfig = {
  rain: {
    label: 'Rain',
    color: STONE[500],
  },
} satisfies ChartConfig;

const mixedChartConfig = {
  snow: {
    label: 'Snow',
    color: STONE[400],
  },
  rain: {
    label: 'Rain',
    color: STONE[500],
  },
} satisfies ChartConfig;

function PrecipitationChart({ forecast }: { forecast: ForecastData }) {
  const hasRain = forecast.totalRainMm > 0;
  const hasSnow = forecast.totalSnowMm > 0;

  const chartData = forecast.periods.map((period: ForecastPeriod) => ({
    time: period.time,
    snow: +(period.snowMm * MM_TO_INCHES).toFixed(3),
    rain: +(period.rainMm * MM_TO_INCHES).toFixed(3),
    pop: Math.round(period.pop * 100),
  }));

  const config = hasRain && hasSnow
    ? mixedChartConfig
    : hasSnow
      ? snowChartConfig
      : rainChartConfig;

  return (
    <ChartContainer config={config} className="aspect-[3/1] w-full">
      <AreaChart
        accessibilityLayer
        data={chartData}
        margin={{ left: -20, right: 4, top: 4, bottom: 0 }}
      >
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis
          dataKey="time"
          tickLine={false}
          axisLine={false}
          tickMargin={8}
          tick={{ fontSize: 11 }}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          tickMargin={4}
          tick={{ fontSize: 10 }}
          tickFormatter={(v: number) => `${v}"`}
        />
        <ChartTooltip
          cursor={false}
          content={({ active, payload }) => {
            if (!active || !payload?.length) return null;
            const data = payload[0].payload;
            return (
              <div className="rounded-lg border border-border/50 bg-background px-3 py-2 text-xs shadow-xl">
                <div className="font-medium">{data.time}</div>
                <div className="mt-1 space-y-0.5 text-muted-foreground">
                  {data.snow > 0 && <div>Snow: {data.snow}"</div>}
                  {data.rain > 0 && <div>Rain: {data.rain}"</div>}
                  <div>{data.pop}% chance</div>
                </div>
              </div>
            );
          }}
        />
        {hasSnow && (
          <Area
            dataKey="snow"
            type="monotone"
            fill="var(--color-snow)"
            fillOpacity={0.15}
            stroke="var(--color-snow)"
            strokeWidth={1.5}
          />
        )}
        {hasRain && (
          <Area
            dataKey="rain"
            type="monotone"
            fill="var(--color-rain)"
            fillOpacity={0.15}
            stroke="var(--color-rain)"
            strokeWidth={1.5}
          />
        )}
      </AreaChart>
    </ChartContainer>
  );
}

function MetricRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-stone-500">{label}</span>
      <span className="text-sm font-medium text-stone-700 tabular-nums">{value}</span>
    </div>
  );
}

function SnowAlertTab({ alerts }: { alerts: WeatherForecastAlert[] }) {
  const [selectedAreaId, setSelectedAreaId] = useState<string>(alerts[0]?.serviceAreaId || '');
  const selectedAlert = alerts.find(a => a.serviceAreaId === selectedAreaId) || alerts[0];

  if (!selectedAlert) return null;

  return (
    <div className="px-6 py-5 space-y-5">
      <p className="text-sm text-stone-500">
        NWS forecast for the next {selectedAlert.forecastPeriods.length * 6} hours in 6-hour periods.
      </p>

      {alerts.length > 1 && (
        <Select value={selectedAlert.serviceAreaId} onValueChange={setSelectedAreaId}>
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {alerts.map(a => (
              <SelectItem key={a.serviceAreaId} value={a.serviceAreaId}>
                {a.serviceAreaName}
                <span className="text-stone-400 ml-1.5">· {a.forecastSnowfallInches}"</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-wider text-stone-400 font-medium">
          Total accumulation
        </span>
        <span className="text-lg font-semibold tabular-nums text-stone-800">
          {selectedAlert.forecastSnowfallInches}"
        </span>
      </div>

      <div className="bg-stone-50 rounded-lg border border-stone-100 p-3 max-h-[360px] overflow-y-auto">
        <ForecastTimeline alert={selectedAlert} />
      </div>
    </div>
  );
}

export function WeatherDetailsModal({
  open,
  onOpenChange,
  weather,
  forecast,
  companyAddress,
  weatherAlerts,
}: WeatherDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<'weather' | 'snow-alert'>('weather');
  const hasAlerts = weatherAlerts && weatherAlerts.length > 0;

  if (!weather) return null;

  const hasPrecip = forecast && (forecast.totalRainIn > 0.01 || forecast.totalSnowIn > 0.01 || forecast.maxPop > 0.1);
  const windDisplay = weather.wind_speed
    ? `${weather.wind_speed} mph${weather.wind_deg ? ` ${getWindDirection(weather.wind_deg)}` : ''}${weather.wind_gust ? ` (gusts ${weather.wind_gust})` : ''}`
    : '—';
  const visDisplay = weather.visibility
    ? `${(weather.visibility / 1609.34).toFixed(1)} mi`
    : '—';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px] max-h-[90vh] overflow-hidden p-0 flex flex-col gap-0">
        <div className="px-6 pt-5 pb-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base font-semibold text-stone-800">
              {activeTab === 'snow-alert' ? (
                <>
                  <CloudSnow className="w-3.5 h-3.5 text-orange-600" />
                  Snowfall Forecast
                </>
              ) : (
                <>
                  <MapPin className="w-3.5 h-3.5 text-stone-400" />
                  {weather.city}, {weather.state}
                </>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Weather details for {weather.city}
            </DialogDescription>
          </DialogHeader>

          {/* Tabs - only show when alerts exist */}
          {hasAlerts && (
            <div className="flex gap-1 mt-3 bg-stone-100 rounded-lg p-0.5">
              <button
                onClick={() => setActiveTab('weather')}
                className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors ${
                  activeTab === 'weather'
                    ? 'bg-white text-stone-800 shadow-sm'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                Weather
              </button>
              <button
                onClick={() => setActiveTab('snow-alert')}
                className={`flex-1 text-xs font-medium py-1.5 rounded-md transition-colors flex items-center justify-center gap-1.5 ${
                  activeTab === 'snow-alert'
                    ? 'bg-white text-stone-800 shadow-sm'
                    : 'text-stone-500 hover:text-stone-700'
                }`}
              >
                <CloudSnow className="h-3 w-3 text-orange-500" />
                Snow Alert
              </button>
            </div>
          )}

          {/* Main weather display - only on weather tab */}
          {activeTab === 'weather' && (
            <div className="mt-4 flex items-center gap-4">
              <span className="text-4xl leading-none">{getWeatherEmoji(weather.condition, weather.icon)}</span>
              <div>
                <div className="text-4xl font-bold tracking-tight text-stone-900 tabular-nums">
                  {weather.temperature}°
                </div>
                <div className="text-sm text-stone-500 capitalize mt-0.5">
                  {weather.description || weather.condition}
                </div>
              </div>
              <div className="ml-auto text-right">
                <div className="text-xs text-stone-400">Feels like</div>
                <div className="text-lg font-semibold text-stone-700 tabular-nums">
                  {weather.feels_like ?? weather.temperature}°
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {activeTab === 'snow-alert' && hasAlerts ? (
            <SnowAlertTab alerts={weatherAlerts} />
          ) : (
            <>
              {/* Precipitation forecast */}
              {forecast && (
                <div className="border-t border-stone-100">
                  <div className="px-6 pt-4 pb-3">
                    <div className="text-[11px] font-medium uppercase tracking-wider text-stone-400">
                      24-Hour Precipitation
                    </div>
                    <p className="text-[13px] text-stone-600 mt-1 leading-snug">
                      {forecast.description}
                    </p>

                    {/* Totals */}
                    {(forecast.totalSnowIn > 0.01 || forecast.totalRainIn > 0.01) && (
                      <div className="flex items-center gap-4 mt-2">
                        {forecast.totalSnowIn > 0.01 && (
                          <div className="flex items-center gap-1.5">
                            <Snowflake className="w-3.5 h-3.5 text-stone-400" />
                            <span className="text-sm font-medium text-stone-700 tabular-nums">
                              {formatPrecipInches(forecast.totalSnowIn)}
                            </span>
                            <span className="text-xs text-stone-400">snow</span>
                          </div>
                        )}
                        {forecast.totalRainIn > 0.01 && (
                          <div className="flex items-center gap-1.5">
                            <CloudRain className="w-3.5 h-3.5 text-stone-500" />
                            <span className="text-sm font-medium text-stone-700 tabular-nums">
                              {formatPrecipInches(forecast.totalRainIn)}
                            </span>
                            <span className="text-xs text-stone-400">rain</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Chart */}
                  {hasPrecip && (
                    <div className="px-4 pb-4">
                      <PrecipitationChart forecast={forecast} />
                    </div>
                  )}
                </div>
              )}

              {/* Conditions */}
              <div className="border-t border-stone-100 px-6 pt-4 pb-5">
                <div className="text-[11px] font-medium uppercase tracking-wider text-stone-400 mb-2">
                  Conditions
                </div>
                <div className="divide-y divide-stone-50">
                  <MetricRow
                    label="High / Low"
                    value={`${weather.temp_max ?? weather.temperature}° / ${weather.temp_min ?? weather.temperature}°`}
                  />
                  <MetricRow label="Wind" value={windDisplay} />
                  <MetricRow label="Humidity" value={`${weather.humidity ?? 0}%`} />
                  <MetricRow label="Visibility" value={visDisplay} />
                  <MetricRow label="Pressure" value={`${weather.pressure ?? 0} mb`} />
                  {weather.clouds !== undefined && (
                    <MetricRow label="Cloud Coverage" value={`${weather.clouds}%`} />
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-stone-100 px-6 py-3">
                <div className="flex items-center gap-1.5 text-[11px] text-stone-400">
                  <MapPin className="w-3 h-3" />
                  {companyAddress}
                </div>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
